import { classNames } from "primereact/utils";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { ITenantModel } from "./models/tenantModel";
import styles from "./TenantsList.module.scss";
import { Icon, IconType } from "../../../../common/icons/Icon";
import {
  constructTenantHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useNavigate } from "react-router-dom";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";

export const TenantsList = () => {
  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    href: routePaths.tenantAdd,
  });

  const { data: tenantsResult, isLoading } = useApiQuery<ITenantModel[]>({
    queryKey: [endpointsMetadata.tenant.getAll],
    endpoint: endpointsMetadata.tenant.getAll,
    globalIsLoading: false,
  });

  return (
    <ItemsList
      isLoading={isLoading}
      className={styles.root}
      numberOfItems={20}
      title="Всички бизнес клиенти"
      noItemsText="Няма намерени бизнес клиенти."
      itemTemplate={(item, index, actionButtons, className) => {
        return (
          <TenantItem
            className={className}
            item={item}
            key={item?.id ?? index}
            isLoading={isLoading}
            actionButtons={actionButtons}
          />
        );
      }}
      items={tenantsResult ?? []}
      swipeLeftActions={[
        {
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да изтриете този бизнес клиент??",
          method: "delete",
          endpoint: endpointsMetadata.tenant.deleteTenant,
          iconType: IconType.DeleteIcon,
          validator: (item: ITenantModel) => !item.isCancelled,
          invalidateKeys: [[endpointsMetadata.tenant.getAll]],
        },
        {
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да възстановите този бизнес клиент?",
          method: "put",
          iconType: IconType.PutBackIcon,
          endpoint: endpointsMetadata.tenant.putTenantBack,
          validator: (item: ITenantModel) => item.isCancelled,
          invalidateKeys: [[endpointsMetadata.tenant.getAll]],
        },
      ]}
      swipeRightActions={[
        {
          type: "edit",
          endpoint: routePaths.tenantEdit,
          iconType: IconType.EditIcon,
          validator: (item: ITenantModel) => !item.isCancelled,
        },
      ]}
      deletedFilter={{
        storageKey: "tenants",
        isDeleted: (item: ITenantModel) => !!item.isCancelled,
        sectionTitle: "Изтрити бизнес клиенти",
        toggleLabel: "Покажи изтритите бизнес клиенти",
      }}
      showItemsCount
      enableSearch
      fullHeight
    />
  );
};

interface ITenantItemProps {
  className?: string;
  item?: ITenantModel;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}

export const TenantItem = ({
  className,
  item,
  isLoading,
  actionButtons,
}: ITenantItemProps) => {
  const navigate = useNavigate();

  const onClick = () => {
    if (item) {
      const href = constructTenantHref(item?.id);
      navigate(href);
    }
  };

  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_cancelled]: item?.isCancelled,
      })}
      onClick={onClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          value={
            <>
              {item?.firstName} {item?.lastName}
              <span className={styles.item_left_name_id}>(ID: {item?.id})</span>
            </>
          }
          width={200}
        />
      </div>

      {actionButtons}
      <Icon type={IconType.RightChevronIcon} />
    </div>
  );
};
